import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerCallFlowExtraDataT } from '../PowerCallFlow';
import AnnounceFromPayload from '../helpers/announceFromPayload';
import { IVR as ActionModal } from '../modals/ivr';
import type { AnnouncementPayloadT } from '../types';

export type Payload = {
  announcement: AnnouncementPayloadT;
  buttons: Record<string, Button>;
  default_node_id: null | string;
  repeat: number;
};

export type Button = {
  button: string;
  nodeId: string;
};

type FlowCallNodeIVRType = FlowNodeTypes.IVR_MENU;
export type FlowCallNodeIVR = NodeT<FlowCallNodeIVRType, Payload>;

const portTranslator = (node: FlowCallNodeIVR): RegisterPortT[] => {
  const { buttons } = node.payload;
  return Object.keys(buttons).map((key: string) => ({
    id: uuid(),
    type: 'out',
    label: `Press ${buttons[key].button}`,
    connection: buttons[key].nodeId,
  }));
};

export class IVRNode extends NodeTypeComponent<
  FlowCallNodeIVR,
  PowerCallFlowExtraDataT
>() {
  static icon: IconNames = 'th';
  static label = 'IVR';
  static helpText =
    'Give incoming callers options to choose from using their dial pad before talking to an agent.';
  static color = flowNodeColor.purple;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.IVR_MENU };
  static updateConnection = (
    node: FlowCallNodeIVR,
    port: InternalPortT,
    newConnection: string,
  ): FlowCallNodeIVR => {
    const payload = { ...node.payload };
    const { buttons } = payload;

    let oldNodeId;
    Object.keys(buttons).find((key: string) => {
      if (buttons[key].nodeId === port.connection) {
        oldNodeId = buttons[key].nodeId;
        payload.buttons[key].nodeId = newConnection;
        return true;
      }
      return false;
    });

    if (oldNodeId === node.payload.default_node_id) {
      payload.default_node_id = newConnection;
    }

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    return (
      <FlowNode
        color={IVRNode.color}
        icon={IVRNode.icon}
        label={IVRNode.label}
        text={
          <AnnounceFromPayload
            node={this.props.node}
            extraData={this.props.extraData}
          />
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
